import React, { FC, useMemo, useState } from 'react';
import { Button, Dropdown, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import CommerciumLoaderHost from 'src/utils/CommerciumLoaderHost';
import { PLATFORM_FIELD } from 'src/interfaces/Platform.interface';
import {
  ImageMappingMeta,
  ImageMappingOptionType,
  ImageMappingRow,
  applyImageMappingOptionChange,
  buildImageMappingRowForSlot,
  buildImageMappingSourceImageOptionLabels,
  defaultSourceImageShortcode,
  isImageMappingSlotRequired,
  listImageMappingSlotsAvailableToAdd,
} from '../utils/listingProfileImageMappingUtils';
import { SectionHeaderLabel } from './SectionHeaderInfoTooltip';
import { LISTING_PROFILE_TOOLTIPS } from './SectionHeaderInfoTooltip';
import ListingProfileFieldSearchSelect from './ListingProfileFieldSearchSelect';

const addLinkButtonClass =
  'btn btn-link btn-sm text-primary fw-bold p-0 product-listing__profile-add-link-btn';

type ListingProfileImageMappingAddNewProps = {
  meta: ImageMappingMeta | null;
  rows: ImageMappingRow[];
  onRowsChange: (rows: ImageMappingRow[]) => void;
};

export const ListingProfileImageMappingAddNew: FC<ListingProfileImageMappingAddNewProps> = ({
  meta,
  rows,
  onRowsChange,
}) => {
  const slotsAvailableToAdd = useMemo(
    () => (meta ? listImageMappingSlotsAvailableToAdd(meta, rows) : []),
    [meta, rows]
  );

  if (!meta || slotsAvailableToAdd.length === 0) {
    return null;
  }

  const addImageMappingRow = (slot: (typeof slotsAvailableToAdd)[number]) => {
    onRowsChange([...rows, buildImageMappingRowForSlot(meta, slot, rows)]);
  };

  if (slotsAvailableToAdd.length === 1) {
    return (
      <button
        type="button"
        className={addLinkButtonClass}
        onClick={() => addImageMappingRow(slotsAvailableToAdd[0])}
      >
        + Add New
      </button>
    );
  }

  return (
    <Dropdown align="start" className="product-listing__image-mapping-add-dropdown">
      <Dropdown.Toggle as="button" type="button" className={addLinkButtonClass} id="image-mapping-add-slot">
        + Add New
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {slotsAvailableToAdd.map((slot) => (
          <Dropdown.Item key={slot.id} onClick={() => addImageMappingRow(slot)}>
            {slot.api_code || slot.api_name}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

type ListingProfileImageMappingBlockProps = {
  loading: boolean;
  meta: ImageMappingMeta | null;
  rows: ImageMappingRow[];
  onRowsChange: (rows: ImageMappingRow[]) => void;
  onDeleteRow?: (row: ImageMappingRow, index: number) => Promise<boolean>;
  unavailableMessage?: string | null;
  showSection?: boolean;
  nativeFields: PLATFORM_FIELD[];
  customFields: PLATFORM_FIELD[];
  mappingFieldsLoading?: boolean;
  sourcePlatformId?: number | null;
  showIsDefaultToggle?: boolean;
  isDefault?: boolean;
  onIsDefaultChange?: (value: boolean) => void;
};

const ListingProfileImageMappingBlock: FC<ListingProfileImageMappingBlockProps> = ({
  loading,
  meta,
  rows,
  onRowsChange,
  onDeleteRow,
  unavailableMessage = null,
  showSection = false,
  nativeFields,
  customFields,
  mappingFieldsLoading = false,
  sourcePlatformId = null,
  showIsDefaultToggle = false,
  isDefault = false,
  onIsDefaultChange,
}) => {
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);

  const sourceImageOptions = useMemo(
    () => (meta ? buildImageMappingSourceImageOptionLabels(meta) : []),
    [meta]
  );

  if (!showSection && !loading) {
    return null;
  }

  if (loading) {
    return (
      <CommerciumLoaderHost
        variant="section"
        layout="row"
        compact
        className="py-3 mt-4 border-top"
        label="Loading image field mapping..."
      />
    );
  }

  const sectionTitle = (
    <div
      className="d-flex align-items-center justify-content-between flex-wrap gap-2"
      style={{ marginBottom: 'calc(6px * 1.15)' }}
    >
      <label className="form-label mb-0">
        <SectionHeaderLabel
          title="Images Mapping (Optional)"
          tooltip={LISTING_PROFILE_TOOLTIPS.imageMapping}
          tooltipId="listing-profile-image-mapping-tooltip"
        />
      </label>
      {showIsDefaultToggle ? (
        <div className="d-flex align-items-center gap-2">
          <span className="small text-gray-700">Default</span>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="image-mapping-is-default-tooltip">Mark as default image mapping rule</Tooltip>}
          >
            <i className="bi bi-info-circle text-muted" style={{ fontSize: '0.85rem' }} />
          </OverlayTrigger>
          <Form.Check
            type="switch"
            id="image-mapping-is-default-switch"
            checked={isDefault}
            onChange={(e) => onIsDefaultChange?.(e.target.checked)}
            label=""
            className="mb-0"
          />
        </div>
      ) : null}
    </div>
  );

  if (!meta) {
    return (
      <div className="mt-4 pt-3 border-top mb-0">
        {sectionTitle}
        <p className="text-muted small mb-0">
          {unavailableMessage ??
            "Image mapping isn't available for this marketplace yet, but it's coming soon. Your images will be sent as-is from your source system. If you need to reorder images or map them from custom fields, contact your account manager and we'll help set it up."}
        </p>
      </div>
    );
  }

  const getDestinationSlotIndex = (row: ImageMappingRow) =>
    meta.destinationSlots.findIndex((slot) => slot.id === row.destinationFieldDataId);

  const updateRow = (index: number, patch: Partial<ImageMappingRow>) => {
    onRowsChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const handleMappingOptionChange = (index: number, option: ImageMappingOptionType) => {
    const row = rows[index];
    if (!row) return;
    const slotIndex = getDestinationSlotIndex(row);
    onRowsChange(
      rows.map((r, i) =>
        i === index ? applyImageMappingOptionChange(r, option, meta, slotIndex) : r
      )
    );
  };

  const handleDeleteRow = async (index: number) => {
    const row = rows[index];
    if (!row || deletingIndex != null) return;
    setDeletingIndex(index);
    try {
      if (onDeleteRow) {
        const ok = await onDeleteRow(row, index);
        if (!ok) return;
      }
      onRowsChange(rows.filter((_, i) => i !== index));
    } finally {
      setDeletingIndex(null);
    }
  };

  return (
    <div className="mt-4 pt-3 border-top mb-0">
      {sectionTitle}
      {rows.length === 0 ? (
        <p className="text-muted small mb-0">
          No image slots mapped. Use + Add New to map marketplace image fields.
        </p>
      ) : (
        <div className="product-listing__more-mappings-grid product-listing__image-mappings-grid">
          <div className="product-listing__image-mappings-grid-header fw-semibold text-gray-600 mb-3">
            <div>Attribute Name</div>
            <div>Mapping Type</div>
            <div className="product-listing__more-mappings-grid-header-value">Attribute Value</div>
          </div>
          {rows.map((row, index) => {
            const required = isImageMappingSlotRequired(meta, row.destinationFieldDataId);
            return (
              <div
                key={`image-mapping-${row.destinationFieldDataId ?? index}`}
                className={`product-listing__image-mapping-grid-row py-3${index === 0 ? ' border-top' : ''}`}
              >
                <div className="fw-semibold text-gray-800 d-flex align-items-center gap-1">
                  <span>{row.attributeLabel}</span>
                  {required ? <span className="text-danger">*</span> : null}
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id={`image-slot-${row.destinationFieldDataId}`}>{row.attributeLabel}</Tooltip>
                    }
                  >
                    <i className="bi bi-info-circle text-muted" style={{ fontSize: '0.85rem' }} />
                  </OverlayTrigger>
                </div>
                <div className="product-listing__more-mapping-grid-mapping-type">
                  <select
                    className="form-select product-listing__more-mapping-control"
                    value={row.mappingOption}
                    onChange={(e) =>
                      handleMappingOptionChange(index, e.target.value as ImageMappingOptionType)
                    }
                  >
                    <option value="DEFAULT">Default</option>
                    <option value="NATIVE">Native Field</option>
                    <option value="CUSTOM">Custom Field</option>
                    {meta.supportsImageFieldMappingType ? (
                      <option value="IMAGE_FIELD">Image Field</option>
                    ) : null}
                  </select>
                </div>
                <div className="product-listing__more-mapping-value-cell">
                  <div className="product-listing__more-mapping-value-cell-inner">
                    <div className="product-listing__more-mapping-value-field">
                      {row.mappingOption === 'DEFAULT' ? (
                        <input
                          type="text"
                          className="form-control"
                          placeholder={defaultSourceImageShortcode(row.position)}
                          value={row.valueStr}
                          onChange={(e) => updateRow(index, { valueStr: e.target.value })}
                        />
                      ) : row.mappingOption === 'IMAGE_FIELD' ? (
                        <select
                          className="form-select product-listing__more-mapping-control"
                          value={row.position > 0 ? row.position : ''}
                          onChange={(e) =>
                            updateRow(index, {
                              position: e.target.value === '' ? 0 : Number(e.target.value),
                              mappedFieldId: meta.imageField?.id ?? null,
                            })
                          }
                        >
                          <option value="">Select source image</option>
                          {sourceImageOptions.map((label, optionIndex) => (
                            <option key={label} value={optionIndex + 1}>
                              {label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="d-flex align-items-center w-100">
                          <ListingProfileFieldSearchSelect
                            key={`${index}-${row.mappingOption}`}
                            name={`image-mapping-field-${index}`}
                            platformId={sourcePlatformId}
                            fieldType={row.mappingOption === 'CUSTOM' ? 'CUSTOM' : 'NATIVE'}
                            value={row.mappedFieldId ?? ''}
                            selectedLabel={
                              (row.mappingOption === 'NATIVE' ? nativeFields : customFields).find(
                                (field) => Number(field.id) === Number(row.mappedFieldId)
                              )?.display_name ?? null
                            }
                            placeholder="Select field"
                            disabled={mappingFieldsLoading}
                            onChange={(e) =>
                              updateRow(index, {
                                mappedFieldId:
                                  e.target.value === '' ? null : Number(e.target.value),
                              })
                            }
                            className="form-select product-listing__more-mapping-control"
                          />
                          {mappingFieldsLoading ? (
                            <CommerciumLoaderHost variant="inline" layout="inline" className="ms-2" />
                          ) : null}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="product-listing__more-mapping-remove-wrap">
                    <Button
                      type="button"
                      variant="link"
                      className="text-danger p-0 ms-2 mt-0 mb-0"
                      disabled={deletingIndex === index}
                      title="Remove this image slot mapping"
                      aria-label="Delete image mapping row"
                      onClick={() => handleDeleteRow(index)}
                    >
                      {deletingIndex === index ? (
                        <CommerciumLoaderHost variant="inline" layout="inline" />
                      ) : (
                        <i className="bi bi-trash" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ListingProfileImageMappingBlock;
